import { formatRangeValues } from 'lib/reportUtils';
import { ExportFilters, ExportData, ExportDataRow, ExportAmountObj, ExportContentObj } from './types';
import { ReportViewModel, ReportColumn, Column } from 'types/report';
import { logger } from './reportNTParser/utils';

export const EXCLUDED_KEYS = ['GL', 'accounts', 'percentOf', 'budgetAvailable', 'style'] as const;
export const SECONDARY_KEYS = ['actuals', 'budget', 'variance'] as const;
const EMPTY_RETURN = ' ';

export const titleCase = (title: string) =>
  title.charAt(0).toUpperCase() + title.slice(1).toLowerCase().replace(/ /g, '_');

export const spacer = (level: number | undefined, spacerString = '    ') => {
  if (level === undefined) return '';
  return level === 0 ? '' : spacerString.repeat(level);
};

type ExportParserProps = {
  transformedData: ReportViewModel | null;
  filters: ExportFilters | null;
  trace?: boolean;
  respectCollapsedRows?: boolean;
  title?: string;
  locationName?: string;
  isConsolidated?: boolean;
  consolidatedLocationNames?: string[];
};

type ParseAmountsProps = {
  money: number;
  percent: number;
  filters?: ExportFilters | null;
  allowMoney?: boolean;
  allowPercentage?: boolean;
};

const parseAmounts = ({ money, percent, filters, allowMoney, allowPercentage }: ParseAmountsProps): ExportAmountObj => {
  if (!allowMoney && !allowPercentage) {
    return { money: EMPTY_RETURN, percent: EMPTY_RETURN };
  }
  const response: ExportAmountObj = {};
  if (allowMoney) {
    response.money = !filters || filters.money ? money : EMPTY_RETURN;
  }
  if (allowPercentage) {
    response.percent = !filters || filters.percentage ? percent : EMPTY_RETURN;
  }
  return response;
};

const generateColumnTitle = (column: Column) => {
  const { start, end } = column.date || {};
  return column.isLocation
    ? column.name
    : column.type[0].type === 'Direct'
      ? start
      : formatRangeValues(start && end ? `${start} - ${end}` : start, 'KS');
};

export const formatValue = (amount: number, decimals: number = 0, locale: string = 'en-US') => {
  return new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
};

export const formatAmount = (
  amount: number | null,
  decimals: number = 0,
  compact: boolean = false,
  unitless: boolean = false
) => {
  if (amount === null) return ' ';
  if (amount === 0) return compact ? '' : ' -- ';
  const units = unitless ? '' : compact ? '$' : '$ ';
  if (amount < 0) {
    return unitless ? formatValue(amount, decimals) : `(${units}${formatValue(Math.abs(amount), decimals)})`;
  }
  return `${units}${formatValue(amount, decimals)}`;
};

// Helper function to check if a row is visible (not collapsed) in the DOM
const isRowVisible = (rowId: string): boolean => {
  // Check if any element with this row ID has the 'hidden' class
  const rowElements = document.querySelectorAll(`[data-row-id="${rowId}"]`);
  if (rowElements.length === 0) {
    // If no DOM elements found, assume row is visible (fallback for cases where DOM isn't available)
    return true;
  }

  // Check if any of the row elements are hidden
  for (const element of rowElements) {
    if (element.classList.contains('hidden')) {
      return false;
    }
  }
  return true;
};

// This function parses the ReportViewModel object to create the intermediate data structure that will be used in all exports.
export const exportParser = ({
  transformedData,
  filters = null,
  trace = false,
  respectCollapsedRows = false,
  title,
  locationName,
  isConsolidated,
  consolidatedLocationNames,
}: ExportParserProps): ExportData | null => {
  //if (trace) console.time('exportParser');
  if (trace) logger.msg('exportParser', { transformedData, filters, respectCollapsedRows });
  if (!transformedData) {
    return null;
  }
  // Filter the columns based on compound column settings and user preferences
  const filteredColumns: Column[] = transformedData.columns.filter((column: Column) => {
    // If it's not a compound column, always include it
    if (!column.compoundColumn) {
      return true;
    }

    // For compound columns, check if user wants them included
    if (column.originType === 'Total' && filters?.total) {
      return true;
    }

    if (column.originType === 'Difference' && filters?.difference) {
      return true;
    }

    // Exclude compound columns that aren't specifically requested
    return false;
  });

  // Backend handles all column reversal - columns are already in correct order from transformedData
  // which is built from report.dates that the backend has already reversed if needed
  if (trace) logger.msg('filteredColumns', filteredColumns);

  // Derive export metadata from available data
  // Get date range from first data column
  const firstDataColumn = filteredColumns.find(col => col.date?.start);
  const dateRange = firstDataColumn?.date
    ? {
        start: firstDataColumn.date.start,
        end: firstDataColumn.date.end || firstDataColumn.date.start,
        range: firstDataColumn.date.end
          ? `${firstDataColumn.date.start} - ${firstDataColumn.date.end}`
          : firstDataColumn.date.start,
      }
    : undefined;

  // Determine if this is a multi-location report
  const isMultiLocation = filteredColumns.some(col => col.isLocation) || false;

  // Get all location names from location columns if multi-location
  const locationColumns = filteredColumns.filter(col => col.isLocation);
  const allLocationNames = locationColumns.map(col => col.name).filter((name): name is string => !!name);
  const multiLocationName = allLocationNames.length > 0 ? allLocationNames[0] : undefined;

  // For consolidated reports, use the provided consolidated location names
  // Otherwise, use location names from location columns (for multi-location non-consolidated reports)
  const finalLocationNames =
    isConsolidated && consolidatedLocationNames && consolidatedLocationNames.length > 0
      ? consolidatedLocationNames
      : allLocationNames.length > 0
        ? allLocationNames
        : undefined;

  const table: ExportData = {
    name: multiLocationName || 'Report',
    title: title,
    locationName: locationName,
    locationNames: finalLocationNames,
    isConsolidated: isConsolidated,
    type: undefined, // Type will need to be passed from caller or derived differently
    timeFrame: undefined, // Timeframe will need to be passed from caller or derived differently
    date: dateRange,

    data: [],
  };

  table.fileName = `KS-Report -- ${multiLocationName || locationName || 'Report'}`;
  const rowCount = filteredColumns[0]?.data?.length || 0;
  const capabilities = transformedData.capabilities;
  const allowMoney = capabilities?.money && (!filters || filters.money);
  const allowPercentage = capabilities?.percentage && (!filters || filters.percentage);
  const hasBudgetData = filteredColumns.some((col: Column) => col.type?.some((t: any) => t.type === 'Budget'));
  const allowBudget = capabilities?.budget && hasBudgetData && (!filters || filters.budget);
  const useSmoothing = capabilities.smoothing && (!filters || filters.smoothing);

  if (trace)
    logger.msg('exportParser', {
      rowCount,
      allowMoney,
      allowPercentage,
      allowBudget,
      useSmoothing,
      trace,
      filters,
    });

  if (rowCount === 0) {
    logger.msg('(silent error) Export:: No data to export');
    return table;
  }
  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
    // Check if we should respect collapsed rows and if this row is visible
    if (respectCollapsedRows) {
      const firstColumnCell = filteredColumns[0]?.data?.[rowIndex];
      if (firstColumnCell?.id && !isRowVisible(firstColumnCell.id)) {
        // Skip this row if it's collapsed/hidden
        continue;
      }
    }

    table.budgetAvailable = transformedData.capabilities?.budget ?? false;
    const row: ExportDataRow = {
      style: { isTotal: false, isHeader: false, isBigHeader: false, level: 0 },
    };

    filteredColumns.forEach((column: Column, index: number) => {
      const firstColumn = index === 0;
      const cell = column.data?.[rowIndex];

      const columnName = column.name ? column.name : column.originType === 'Total' ? 'Total' : undefined;

      if (column && columnName && cell) {
        const title = generateColumnTitle(column);
        if (firstColumn) {
          row.GL = cell.glCode ? cell.glCode : EMPTY_RETURN;
          //Let's add row specific properties (not renderable)
          row.style.isTotal = cell.vizHelpers?.isTotal;
          row.style.isHeader = cell.vizHelpers?.isHeader;
          row.style.isBigHeader = cell.vizHelpers?.isBigHeader;
          row.style.level = cell.level;

          row.accounts = (useSmoothing ? '*' : '') + cell.amountTitle;
          if (allowPercentage) row.percentOf = cell.percentTitle ? cell.percentTitle : EMPTY_RETURN;
        } else {
          const content: ExportContentObj = {};
          if (table.budgetAvailable) {
            if (!filters || filters.actuals) {
              content.actuals = parseAmounts({
                money: !useSmoothing ? cell.amount : cell.amountAVG,
                percent: !useSmoothing ? cell.percent : cell.percentAVG,
                filters,
                allowMoney,
                allowPercentage,
              });
            }
            if (allowBudget) {
              content.budget = parseAmounts({
                money: cell.budget,
                percent: cell.budgetPercent,
                filters,
                allowMoney,
                allowPercentage,
              });

              if (allowBudget && filters && filters.variance && filters.actuals) {
                content.variance = parseAmounts({
                  money: cell.difference,
                  percent: cell.variance,
                  filters,
                  allowMoney,
                  allowPercentage,
                });
              }
            }
          } else {
            content.actuals = parseAmounts({
              money: !useSmoothing ? cell.amount : cell.amountAVG,
              percent: !useSmoothing ? cell.percent : cell.percentAVG,
              filters,
              allowMoney,
              allowPercentage,
            });
          }
          // Finally we add the content to the row. The title is the column name.
          row[title || columnName] = content;
        }
      }
    });
    table.data.push(row);
  }
  //if (trace) console.timeEnd('exportParser');
  return table;
};
